/**
 * Gallery — photos by retreat day (REDESIGN.md §7).
 *
 * Mono headers, a three-column grid, and one wide frame per day so a wall of
 * squares has somewhere to rest. Every thumbnail paints its blur preview first,
 * so the grid is laid out before a single photo arrives and nothing jumps.
 *
 * No likes and no comments — that is a rule, not an omission.
 */

import { emptyState } from '../components/empty.js'
import { openLightbox } from '../components/lightbox.js'
import { text } from '../js/html.js'

export function renderGallery({ gallery, retreat }) {
  if (gallery === null) {
    return `<p class="lede">Loading…</p>`
  }

  const days = gallery.days ?? []
  // The retreat's own word for where it is. "Island" was true of exactly one.
  const place = retreat?.place_noun || 'retreat'

  if (days.length === 0) {
    return `
      <p class="eyebrow">${text(place.toUpperCase())} ARCHIVE</p>
      <h1 class="h1">Gallery</h1>
      ${emptyState({
        icon: 'labGallery',
        title: `The photos come from the ${place}`,
        body: 'Laid out by day, with nothing to like and nothing to comment.',
      })}
    `
  }

  return `
    <p class="eyebrow">${text(place.toUpperCase())} ARCHIVE</p>
    <h1 class="h1">Gallery</h1>
    <p class="sub">Captured for you, so you do not reach for the phone.</p>

    ${days.map(renderDay).join('')}
  `
}

function renderDay(day) {
  const count = day.items.length

  return `
    <div class="dayhead">
      <span class="dayhead__day">${day.day_no === null ? 'Before the trip' : `Day ${day.day_no}`}</span>
      <span class="dayhead__count">${count} ${count === 1 ? 'photo' : 'photos'}</span>
    </div>
    <div class="grid">
      ${day.items.map((item, index) => tile(item, index === 0)).join('')}
    </div>
  `
}

/** The first frame of a day runs the full width; the rest share two columns. */
function tile(item, wide) {
  return `
    <button class="ph ${wide ? 'ph--hero' : ''}" data-photo="${item.id}"
            aria-label="${text(item.caption ?? 'Photograph')}">
      <img class="ph__blur" src="${text(item.blur)}" alt="" aria-hidden="true" />
      <img class="ph__image" src="${text(item.thumb_url)}" alt="${text(item.caption)}"
           loading="lazy" decoding="async" onload="this.classList.add('is-loaded')" />
    </button>
  `
}

/** Wires the tiles to the lightbox; called after the screen is in the DOM. */
export function mountGallery(root, { gallery }) {
  const items = (gallery?.days ?? []).flatMap((day) => day.items)

  root.querySelectorAll('[data-photo]').forEach((tile) => {
    tile.addEventListener('click', () => {
      const index = items.findIndex((item) => item.id === Number(tile.dataset.photo))
      openLightbox(items, Math.max(0, index))
    })
  })
}
